import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  isMemberOfAny,
  securityGroupCheckConstraintEmpty,
} from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { User } from '@totem/components/users/container/types';
import UserDetailsContext from '@totem/components/users/details/UserDetailsContext';
import { OrganizationHierarchy } from '@totem/types/organization';
import {
  DataAccessGroup,
  DataAccessPermissionUpdate,
  EligibleSecurityGroup,
  SecurityGroup,
} from '@totem/types/user';
import {
  USER_RESTRICTIONS_ENDPOINT,
  USERS_ELIGIBLE_GROUPS_ENDPOINT,
  USERS_ENDPOINT,
} from '@totem/utilities/endpoints';

type Props = {
  userId: string;
  children?: ReactNode;
};

const UserDetailsContainer = ({ userId, children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [user, setUser] = useState<User>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [securityGroups, setSecurityGroups] = useState<SecurityGroup[]>([]);
  const [securityGroupsLoading, setSecurityGroupsLoading] =
    useState<boolean>(true);
  const [eligibleGroups, setEligibleGroups] = useState<EligibleSecurityGroup[]>(
    [],
  );
  const [orgHierarchy, setOrgHierarchy] = useState<OrganizationHierarchy>(null);
  const [dataPermissions, setDataPermissions] = useState<DataAccessGroup[]>([]);
  const [allDataPermissions, setAllDataPermissions] = useState<
    DataAccessGroup[]
  >([]);
  const [dataPermissionsHierarchy, setDataPermissionsHierarchy] =
    useState<OrganizationHierarchy>(null);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [refreshCount, setRefreshCount] = useState<number>(0);

  useEffect(() => {
    setUserLoading(true);

    fetch(`${USERS_ENDPOINT}/${userId}`, {
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: User) => {
        setUser(result);

        setUserLoading(false);
      });
  }, [userId, getApiToken, refreshCount]);

  useEffect(() => {
    setSecurityGroupsLoading(true);

    fetch(`${USERS_ENDPOINT}/${userId}/securityGroups`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: SecurityGroup[]) => {
        setSecurityGroups(result);

        setSecurityGroupsLoading(false);
      });
  }, [userId, getApiToken, refreshCount]);

  useEffect(() => {
    const hasAssignablePermission = (grp: EligibleSecurityGroup) => {
      if (grp.assignerRole <= userProfile.organization.role) {
        if (
          isMemberOfAny(
            userProfile,
            grp.assignerSecurityGroups,
            securityGroupCheckConstraintEmpty,
          )
        ) {
          return true;
        }
      }
      return false;
    };

    fetch(USERS_ELIGIBLE_GROUPS_ENDPOINT, {
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: EligibleSecurityGroup[]) => {
        setEligibleGroups(result.filter(hasAssignablePermission));
      });
  }, [userProfile, getApiToken]);

  useEffect(() => {
    fetch(`${USER_RESTRICTIONS_ENDPOINT}/`, {
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        setOrgHierarchy(result.hierarchy);
      });
  }, [getApiToken]);

  useEffect(() => {
    fetch(`${USERS_ENDPOINT}/${userId}/dataAccessPermissions`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: DataAccessGroup[]) => {
        setDataPermissions(result);
      });
  }, [userId, getApiToken, refreshCount]);

  useEffect(() => {
    fetch(`${USERS_ENDPOINT}/dataAccessPermissions`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: DataAccessGroup[]) => {
        setAllDataPermissions(result);
      });
  }, [getApiToken]);

  useEffect(() => {
    fetch(`${USERS_ENDPOINT}/organizationHierarchy`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: OrganizationHierarchy) => {
        setDataPermissionsHierarchy(result);

        setDataLoading(false);
      });
  }, [getApiToken]);

  const onRefresh = () => {
    setRefreshCount((prev) => prev + 1);
  };

  const onSecurityGroupAdd = useCallback(
    async (group: SecurityGroup) => {
      if (isSending) return;
      setIsSending(true);

      fetch(`${USERS_ENDPOINT}/${userId}/securityGroups`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(group),
      })
        .then((res) => res.json())
        .then((result: SecurityGroup[]) => {
          setSecurityGroups(result);

          setIsSending(false);
        });
    },
    [isSending, userId, getApiToken],
  );

  const onSecurityGroupRemove = useCallback(
    async (group: SecurityGroup) => {
      if (isSending) return;
      setIsSending(true);

      fetch(`${USERS_ENDPOINT}/${userId}/securityGroups`, {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(group),
      })
        .then((res) => res.json())
        .then((result: SecurityGroup[]) => {
          setSecurityGroups(result);

          setIsSending(false);
        });
    },
    [isSending, userId, getApiToken],
  );

  const onDataPermissionUpdate = useCallback(
    async (update: DataAccessPermissionUpdate) => {
      if (isSending) return;
      setIsSending(true);

      fetch(`${USERS_ENDPOINT}/${userId}/dataAccessPermissions`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(update),
      })
        .then((res) => res.json())
        .then((result: DataAccessGroup[]) => {
          setDataPermissions(result);

          setIsSending(false);
        });
    },
    [isSending, userId, getApiToken],
  );

  return (
    <UserDetailsContext.Provider
      value={{
        userId,
        user,
        userLoading,
        securityGroups,
        securityGroupsLoading,
        eligibleGroups,
        orgHierarchy,
        dataPermissions,
        allDataPermissions,
        dataPermissionsHierarchy,
        dataLoading,
        onRefresh,
        onSecurityGroupAdd,
        onSecurityGroupRemove,
        onDataPermissionUpdate,
      }}
    >
      {children}
    </UserDetailsContext.Provider>
  );
};

export default UserDetailsContainer;
